import { FunctionComponent, useState, PropsWithChildren } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { ErrorMessage } from 'shared/ErrorMessage';
import { TeamNode, BillingProfileNode } from 'typeDeclarations/graphql/nodes';
import { WalletAutoTopupBillingProfileData } from './fragments';
import { AutoTopUpAccordionForm } from './AutoTopUpAccordionForm/AutoTopUpAccordionForm';
import { AutoTopUpSwitch } from './AutoTopUpSwitch/AutoTopUpSwitch';
import { Accordion, AccordionSummary, AccordionDetails, LinearProgress } from '@mui/material';

interface WalletAutoTopupQueryData {
  sessionTeam: Pick<TeamNode, 'id' | 'modified'> & {
    billingProfile: WalletAutoTopupBillingProfileData & Pick<BillingProfileNode, 'id' | 'modified'>;
  };
}

const WALLET_AUTO_TOPUP_QUERY = gql(/* GraphQL */ `
  query walletAutoTopupQuery {
    sessionTeam {
      id
      modified
      billingProfile {
        id
        modified
        ...WalletAutoTopUpAccordionBillingProfileFragment
      }
    }
  }
`);

interface WalletAutoTopupProps {
  disabled: boolean;
}

export const WalletAutoTopup: FunctionComponent<PropsWithChildren<WalletAutoTopupProps>> = ({ disabled }) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const { data, loading, error } = useQuery<WalletAutoTopupQueryData>(WALLET_AUTO_TOPUP_QUERY);

  if (loading) return <LinearProgress />;
  if (error || !data) return <ErrorMessage />;

  const { billingProfile } = data.sessionTeam;

  const toggleExpanded = () => setExpanded((prevState) => !prevState);

  const closePanel = () => setExpanded(false);

  return (
    <Accordion
      elevation={0}
      expanded={expanded}
      disabled={disabled}
      onChange={toggleExpanded}
      sx={{
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary id="recurring-payment-header" aria-controls="recurring-payment-content" sx={{ pl: 0 }}>
        <AutoTopUpSwitch disabled={disabled} expanded={expanded} billingProfile={billingProfile} />
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, flexDirection: 'column' }}>
        <AutoTopUpAccordionForm onClose={closePanel} billingProfile={billingProfile} />
      </AccordionDetails>
    </Accordion>
  );
};
